import * as Sentry from "@sentry/react";

// Suppress repeats of the same (host, kind) within this window.
// Short enough to preserve escalation signal during a long outage,
// long enough to collapse a typical retry burst into a single Sentry event.
const DEDUP_WINDOW_MS = 10_000;
const lastReported = new Map();

// Cached so reportNetworkError can fall back to it as a tag even if
// Sentry's user/tag scope has been cleared elsewhere.
let shopOriginCache = null;

export function setSentryShopContext(shopOrigin) {
  if (!shopOrigin) return;
  shopOriginCache = shopOrigin;
  Sentry.setUser({ id: shopOrigin });
  Sentry.setTag("shop", shopOrigin);
}

export function getNetworkContext() {
  const c =
    (typeof navigator !== "undefined" &&
      (navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection)) ||
    null;
  return {
    onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
    effectiveType: c && c.effectiveType ? c.effectiveType : null,
    downlink: c && typeof c.downlink === "number" ? c.downlink : null,
    rtt: c && typeof c.rtt === "number" ? c.rtt : null,
    saveData: c && typeof c.saveData === "boolean" ? c.saveData : null,
  };
}

export function getResourceTiming(url) {
  try {
    if (
      !url ||
      typeof performance === "undefined" ||
      typeof performance.getEntriesByName !== "function"
    ) {
      return null;
    }
    const entries = performance.getEntriesByName(url);
    if (!entries || entries.length === 0) return null;
    const e = entries[entries.length - 1];
    return {
      duration: e.duration,
      startTime: e.startTime,
      domainLookupStart: e.domainLookupStart,
      domainLookupEnd: e.domainLookupEnd,
      connectStart: e.connectStart,
      connectEnd: e.connectEnd,
      secureConnectionStart: e.secureConnectionStart,
      requestStart: e.requestStart,
      responseStart: e.responseStart,
      responseEnd: e.responseEnd,
      transferSize: e.transferSize,
      nextHopProtocol: e.nextHopProtocol,
    };
  } catch (_) {
    return null;
  }
}

const STAGE_SEGMENTS = new Set(["prod", "dev", "staging"]);

function parseUrl(url) {
  try {
    const u = new URL(url, window.location.href);
    const seg = u.pathname.split("/").filter(Boolean);
    // Skip the API Gateway stage segment so api_path is e.g. "tasks", not "prod".
    const first = seg[0] && STAGE_SEGMENTS.has(seg[0]) ? seg[1] : seg[0];
    return { host: u.host, path: first || "(root)" };
  } catch (_) {
    return { host: "(unparseable)", path: "(unparseable)" };
  }
}

export function classifyNetworkError(error, url) {
  const name = (error && error.name) || "";
  const msg = (error && error.message) || String(error || "");

  if (name === "AbortError" || /\baborted\b/i.test(msg)) return "aborted";
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }
  if (/CORS|cross-origin|preflight/i.test(msg)) return "cors";

  const t = getResourceTiming(url);
  if (t) {
    // The browser sets these to 0 when the corresponding phase never completed.
    if (t.domainLookupStart > 0 && t.domainLookupEnd === 0) return "dns";
    if (t.secureConnectionStart > 0 && t.connectEnd === 0) return "tls";
    if (t.connectStart > 0 && t.connectEnd === 0) return "tcp";
    if (t.requestStart > 0 && t.responseStart === 0) return "no_response";
  }
  return "unknown";
}

export function reportNetworkError(error, options) {
  if (!error) return;
  const opts = options || {};
  const url = opts.url || "";
  const method = opts.method || "GET";
  const source = opts.source || "unknown";
  const operationName = opts.operationName || null;

  // Aborts are not real errors — the user navigated away or we cancelled.
  if (
    error.name === "AbortError" ||
    /\baborted\b/i.test((error && error.message) || "")
  ) {
    return;
  }

  const parsed = parseUrl(url);
  const api_host = parsed.host;
  const api_path = parsed.path;
  const error_kind = classifyNetworkError(error, url);
  const error_name = (error && error.name) || "Error";

  // Rate limit: collapse bursts of (host, kind) into a single Sentry event.
  const dedupKey = `${api_host}|${error_kind}`;
  const now = Date.now();
  const prev = lastReported.get(dedupKey);
  if (prev && now - prev < DEDUP_WINDOW_MS) {
    Sentry.addBreadcrumb({
      category: "network-error.suppressed",
      level: "warning",
      message: `${error_name} ${api_host}${url ? " " + url : ""}`,
      data: { api_host, api_path, error_kind, source, operationName },
    });
    return;
  }
  lastReported.set(dedupKey, now);

  const network = getNetworkContext();
  const timing = getResourceTiming(url);

  let visibilityState = null;
  let navigationType = null;
  let timeSincePageLoad = null;
  try {
    if (typeof document !== "undefined") {
      visibilityState = document.visibilityState;
    }
    if (
      typeof performance !== "undefined" &&
      typeof performance.getEntriesByType === "function"
    ) {
      const navEntries = performance.getEntriesByType("navigation");
      if (navEntries && navEntries[0]) navigationType = navEntries[0].type;
    }
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      timeSincePageLoad = Math.round(performance.now());
    }
  } catch (_) {
    // ignore
  }

  const tags = {
    api_host,
    api_path,
    error_name,
    error_kind,
    online: String(network.onLine),
    source,
  };
  if (shopOriginCache) tags.shop = shopOriginCache;

  Sentry.captureException(error, {
    fingerprint: ["network-error", api_host, error_kind],
    tags,
    contexts: {
      network,
      request: { url, method, operationName },
      timing: timing || { available: false },
      page: { visibilityState, navigationType, timeSincePageLoad },
    },
  });
}

// Used by Sentry.init's beforeSend hook to drop the bare unstructured
// console.error events that the apollo errorLink used to emit. We now
// send structured events via reportNetworkError instead, so these would
// be duplicates with none of the diagnostic tags.
export function isBareConsoleNetworkError(event) {
  const msg =
    (event && event.message) ||
    (event &&
      event.exception &&
      event.exception.values &&
      event.exception.values[0] &&
      event.exception.values[0].value) ||
    "";
  return /^\[Network error\]:/.test(msg) || /^\[GraphQL error\]:/.test(msg);
}
